import React, { useEffect, useState } from "react";
import {
  maintenanceApi,
  ProcDeviceDetail,
  ProcOnboardResult,
  StationDropdownItem,
  SocketTypeItem,
} from "../../../api/maintenance";
import { errMsg } from "./procUtils";
import { Lock, Trash2 } from "lucide-react";
import { useNotice } from "../shared/useNotice";

interface Props {
  device: ProcDeviceDetail;
  onClose: () => void;
  onDone: (res: ProcOnboardResult) => void;
}

interface ConnectorRow {
  connector_name: string;
  socket_type_id: string;
  capacity_kwh: string;
}

/**
 * Pool-first onboarding.
 *
 * The identity (device id / unique name / WebSocket URL) comes from the
 * procurement record and is not editable or even sent by the client — that is
 * the whole point, it can never be mistyped. Only deployment details are filled
 * in here.
 */
const OnboardForm: React.FC<Props> = ({ device, onClose, onDone }) => {
  const [stations, setStations] = useState<StationDropdownItem[]>([]);
  const [socketTypes, setSocketTypes] = useState<SocketTypeItem[]>([]);
  const [displayName, setDisplayName] = useState("");
  const [ocppVersion, setOcppVersion] = useState("1.6");
  const [stationId, setStationId] = useState("");
  const [stationPosition, setStationPosition] = useState(0);
  const [connectors, setConnectors] = useState<ConnectorRow[]>([
    { connector_name: "1", socket_type_id: "", capacity_kwh: "" },
  ]);
  const [error, setError] = useNotice("error", "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([maintenanceApi.getStationsDropdown(), maintenanceApi.getSocketTypes()])
      .then(([st, so]) => {
        setStations(st.data);
        setSocketTypes(so.data);
      })
      .catch(console.error);
  }, []);

  const updateConnector = (i: number, field: keyof ConnectorRow, value: string) =>
    setConnectors((prev) => prev.map((c, idx) => (idx === i ? { ...c, [field]: value } : c)));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      const res = await maintenanceApi.procOnboardDevice(device.id, {
        display_name: displayName.trim() || undefined,
        ocpp_version: ocppVersion,
        station_id: stationId ? Number(stationId) : undefined,
        station_position: stationId ? stationPosition : undefined,
        connectors: connectors
          .filter((c) => c.connector_name.trim())
          .map((c) => ({
            connector_name: c.connector_name.trim(),
            socket_type_id: c.socket_type_id ? Number(c.socket_type_id) : undefined,
            capacity_kwh: c.capacity_kwh ? parseFloat(c.capacity_kwh) : undefined,
          })),
      });
      onDone(res.data);
    } catch (err: any) {
      setError(errMsg(err, "Failed to onboard device"));
      setSaving(false);
    }
  };

  return (
    <div className="maint-form-overlay" onClick={onClose}>
      <div
        className="maint-form-card"
        style={{ width: 640 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="maint-form-header">
          <h3>Onboard {device.unique_id}</h3>
          <button className="maint-btn-icon" onClick={onClose}>
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* identity — read-only, straight off the procurement record */}
          <div className="proc-group">
            <div className="gh">
              <span className="proc-tag auto">Auto · from procurement</span> Identity
            </div>
            <div className="gb proc-locked">
              <div className="maint-form-row">
                <label>
                  Unique name
                  <input value={device.unique_id} disabled />
                </label>
                <label>
                  OCPP device ID
                  <input value={device.unique_id} disabled />
                </label>
              </div>
              <label>
                WebSocket address
                <input value={device.ws_url} disabled />
              </label>
              <div
                className="proc-note"
                style={{ display: "flex", alignItems: "center", gap: 6, color: "#8a8fd0" }}
              >
                <Lock size={12} /> Locked — taken from the procurement record so it can never be
                mistyped.
              </div>
            </div>
          </div>

          {/* what you actually fill in */}
          <div className="proc-group">
            <div className="gh">
              <span className="proc-tag req">You fill this</span> Deployment
            </div>
            <div className="gb">
              <div className="maint-form-row">
                <label>
                  Display name
                  <input
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="e.g. Prestige P2-01"
                  />
                </label>
                <label>
                  OCPP version
                  <select value={ocppVersion} onChange={(e) => setOcppVersion(e.target.value)}>
                    <option value="1.6">1.6</option>
                    <option value="2.0">2.0</option>
                    <option value="2.0.1">2.0.1</option>
                  </select>
                </label>
              </div>

              <div className="maint-form-row">
                <label>
                  Station (optional)
                  <select value={stationId} onChange={(e) => setStationId(e.target.value)}>
                    <option value="">No station</option>
                    {stations.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name} ({s.org_name})
                      </option>
                    ))}
                  </select>
                </label>
                {stationId && (
                  <label style={{ flex: "0 0 120px" }}>
                    Position
                    <input
                      type="number"
                      value={stationPosition}
                      onChange={(e) => setStationPosition(Number(e.target.value))}
                    />
                  </label>
                )}
              </div>

              <label style={{ marginBottom: 6 }}>Connectors</label>
              {connectors.map((c, i) => (
                <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                  <label style={{ flex: "0 0 80px" }}>
                    Name
                    <input
                      value={c.connector_name}
                      onChange={(e) => updateConnector(i, "connector_name", e.target.value)}
                    />
                  </label>
                  <label style={{ flex: 1 }}>
                    Socket type
                    <select
                      value={c.socket_type_id}
                      onChange={(e) => updateConnector(i, "socket_type_id", e.target.value)}
                    >
                      <option value="">Select…</option>
                      {socketTypes.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label style={{ flex: "0 0 100px" }}>
                    kWh
                    <input
                      type="number"
                      step="any"
                      value={c.capacity_kwh}
                      onChange={(e) => updateConnector(i, "capacity_kwh", e.target.value)}
                    />
                  </label>
                  <button
                    type="button"
                    className="maint-btn-icon"
                    style={{ marginBottom: 12 }}
                    disabled={connectors.length <= 1}
                    onClick={() => setConnectors((prev) => prev.filter((_, idx) => idx !== i))}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="maint-btn-small"
                onClick={() =>
                  setConnectors((prev) => [
                    ...prev,
                    { connector_name: String(prev.length + 1), socket_type_id: "", capacity_kwh: "" },
                  ])
                }
              >
                + Add connector
              </button>
              <div className="proc-note">
                Connector <b>0</b> (the device-level one) is created automatically — you don't add it.
              </div>
            </div>
          </div>

          {error && <p className="maint-form-error">{error}</p>}

          <div className="maint-form-actions">
            <button type="button" className="maint-btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="maint-btn-primary" disabled={saving}>
              {saving ? "Onboarding…" : "Onboard device"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OnboardForm;
